import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SelectOrgPrompt, useNeedsOrg } from '../components/SelectOrgPrompt';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Users, UserPlus, Mail, Trash2, Shield, ChevronDown, Lock, UserX, UserCheck, Building2 } from 'lucide-react';
import { isDemoMode } from '../components/DemoBanner';
import DemoUpgradePrompt from '../components/DemoUpgradePrompt';
import UpgradeLimitPrompt from '../components/UpgradeLimitPrompt';
import RolePermissionsTooltip from '../components/RolePermissionsTooltip';
import OrgAccessTab from '../components/OrgAccessTab';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

import { API_BASE as API } from '../config';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'member', label: 'Member' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

const ROLE_COLORS = {
  super_admin: 'bg-red-100 text-red-700',
  admin: 'bg-purple-100 text-purple-700',
  manager: 'bg-blue-100 text-blue-700',
  member: 'bg-green-100 text-green-700',
  viewer: 'bg-gray-100 text-gray-600',
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  member: 'Member',
  viewer: 'Viewer',
};

export default function UserManagement() {
  const { user, hasRole, selectedTenants, userOrgs, operatorContext, activeOrgId } = useAuth();
  const needsOrg = useNeedsOrg();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteOrgId, setInviteOrgId] = useState('');
  // 'teammate' = invite into the Provider tenant; 'customer' = invite into the
  // Solo customer tenant linked to a specific Org. Only meaningful for Provider
  // Admins who have at least one linked Org.
  const [inviteMode, setInviteMode] = useState('teammate');
  const [inviting, setInviting] = useState(false);
  const [showDemoPrompt, setShowDemoPrompt] = useState(false);
  const [limitPrompt, setLimitPrompt] = useState({ open: false, message: '' });
  const DEMO_EMAILS = ['demo@allaboutclaims.com', 'demo-provider@allaboutclaims.com'];
  const isDemo = isDemoMode() && DEMO_EMAILS.includes(user?.email);
  const isProvider = user?.account_type === 'provider';
  const isOperator = user?.is_platform_operator;
  const inContext = isOperator && operatorContext?.tenantId;
  const isInOrgView = isProvider && !!activeOrgId;
  const activeOrg = isInOrgView ? userOrgs.find(o => o.org_id === activeOrgId) : null;

  useEffect(() => {
    // Managers see only the Org Access tab, which loads its data from
    // /api/users/access-matrix. The flat /api/users endpoint is admin-only,
    // so we'd 403 and surface a misleading "Failed to load users" toast.
    const isManagerOnly = user?.role === 'manager';
    if (!isManagerOnly) fetchUsers();
    if (!isManagerOnly && (!isOperator || inContext)) fetchInvites();
  }, [selectedTenants, operatorContext?.tenantId, activeOrgId, user?.role]);

  const fetchUsers = async () => {
    try {
      const endpoint = (isOperator && !inContext) ? `${API}/operator/users` : `${API}/users`;
      const params = isInOrgView ? { org_id: activeOrgId } : {};
      const res = await axios.get(endpoint, { params, withCredentials: true });
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load users');
    }
  };

  const fetchInvites = async () => {
    try {
      const res = await axios.get(`${API}/users/invites`, { withCredentials: true });
      setInvites(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (isDemo) {
      setShowDemoPrompt(true);
      return;
    }
    // Check subscription limit based on role
    try {
      const resource = inviteRole === 'admin' ? 'admin' : inviteRole === 'viewer' ? null : 'user';
      if (resource) {
        const res = await axios.get(`${API}/billing/check-limit/${resource}`, { withCredentials: true });
        if (!res.data.allowed) {
          setLimitPrompt({ open: true, message: res.data.reason });
          return;
        }
        // Check billing impact for active paid subscriptions
        try {
          const impactRes = await axios.post(`${API}/billing/impact`, {
            resource_type: resource,
            action: 'add',
          }, { withCredentials: true });
          if (impactRes.data.has_impact) {
            if (!window.confirm(`${impactRes.data.message}\n\nProceed with invite?`)) return;
          }
        } catch (err) { console.warn('[UserManagement] billing impact check failed (proceeding)', err); }
      }
    } catch (err) { console.warn('[UserManagement] limit precheck failed (proceeding)', err); }
    setInviting(true);
    try {
      const res = await axios.post(`${API}/users/invite`, {
        email: inviteEmail,
        role: inviteRole,
        name: inviteName || undefined,
        // Two cleanly separated invite paths (replaces the old conditional):
        //   • Teammate (or Solo/operator)  → org_id omitted; user joins caller's tenant
        //   • Customer (Provider only)      → org_id present + that Org has linked_tenant_id;
        //                                     backend routes the user into the linked Solo tenant
        //   • In-Org-view shortcut          → activeOrgId still wins (unchanged behaviour)
        org_id: isInOrgView
          ? activeOrgId
          : (isProvider && inviteMode === 'customer' && inviteOrgId ? inviteOrgId : undefined),
      }, { withCredentials: true });
      const emailSent = res.data?.email_sent;
      toast.success(emailSent ? `Invite email sent to ${inviteEmail}` : `Invite created for ${inviteEmail} (email delivery pending)`);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('member');
      setInviteOrgId('');
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success('Role updated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveUser = async (userId, userName, userRole) => {
    // Check billing impact before confirmation
    let impactMsg = '';
    try {
      const resource = (userRole === 'admin' || userRole === 'super_admin') ? 'admin' : (userRole === 'member' || userRole === 'manager') ? 'user' : null;
      if (resource) {
        const res = await axios.post(`${API}/billing/impact`, {
          resource_type: resource,
          action: 'remove',
        }, { withCredentials: true });
        if (res.data.has_impact) {
          impactMsg = `\n\n${res.data.message}`;
        }
      }
    } catch (err) { console.warn('[UserManagement] remove-user impact check failed', err); }
    if (!window.confirm(`Remove ${userName} from this organization?${impactMsg}`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success('User removed');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/users/invites/${inviteId}`, { withCredentials: true });
      toast.success('Invite deleted');
      fetchInvites();
    } catch (err) {
      toast.error('Failed to delete invite');
    }
  };

  const handleToggleStatus = async (userId, userName, currentlyActive) => {
    const action = currentlyActive ? 'deactivate' : 'activate';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${userName}?`)) return;
    try {
      const res = await axios.put(`${API}/users/${userId}/status`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  // Orgs that have a linked Solo customer tenant — only these are eligible
  // for the "Invite a customer user" mode.
  const linkedOrgs = useMemo(
    () => (userOrgs || []).filter((o) => !!o.linked_tenant_id),
    [userOrgs]
  );
  const showCustomerInviteMode = isProvider && !isInOrgView && linkedOrgs.length > 0;

  const roleOptions = (isInOrgView || (isProvider && inviteMode === 'customer'))
    ? ROLE_OPTIONS.filter(r => !['admin', 'super_admin'].includes(r.value))
    : ROLE_OPTIONS;

  if (needsOrg && !isOperator) return <SelectOrgPrompt />;

  return (
    <div className="min-h-screen bg-gray-200 p-4 md:p-8" data-testid="user-management-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            {isInOrgView ? `${activeOrg?.name || 'Organization'} — User Management` : isOperator && !inContext ? 'All About Claims — User Management' : inContext ? `${operatorContext.tenantName} — User Management` : 'User Management'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isInOrgView ? 'Manage users for this organization. New invites will be added to the linked tenant.' : isOperator && !inContext ? 'Manage platform operator team members' : 'Manage team members and invitations'}
          </p>
        </div>

        {/* Provider tenants get tabs (Team / Org Access). Non-providers + operator views stay flat.
            Managers see only the Org Access tab (no invite / team management). */}
        {isProvider && !(isOperator && !inContext) ? (
          <Tabs defaultValue={user?.role === 'manager' ? 'org-access' : 'team'} className="space-y-4" data-testid="user-management-tabs">
            <TabsList className="h-10">
              {user?.role !== 'manager' && (
                <TabsTrigger value="team" className="px-4" data-testid="tab-team">Team</TabsTrigger>
              )}
              <TabsTrigger value="org-access" className="px-4" data-testid="tab-org-access">Org Access</TabsTrigger>
            </TabsList>
            {user?.role !== 'manager' && (
              <TabsContent value="team" className="space-y-6">
                {renderInviteForm()}
                {renderTeamMembers()}
                {renderPendingInvites()}
              </TabsContent>
            )}
            <TabsContent value="org-access">
              <OrgAccessTab currentUserId={user?.user_id} />
            </TabsContent>
          </Tabs>
        ) : (
          <>
            {renderInviteForm()}
            {renderTeamMembers()}
            {renderPendingInvites()}
          </>
        )}
      </div>

      {/* Demo Upgrade Prompt */}
      <DemoUpgradePrompt
        isOpen={showDemoPrompt}
        onClose={() => setShowDemoPrompt(false)}
        featureName="Inviting team members"
      />
      <UpgradeLimitPrompt
        isOpen={limitPrompt.open}
        onClose={() => setLimitPrompt({ open: false, message: '' })}
        message={limitPrompt.message}
      />
    </div>
  );

  function renderInviteForm() {
    if (isOperator && !inContext) return null;
    const isCustomerMode = isProvider && !isInOrgView && inviteMode === 'customer';
    // Helper copy under the title that explains what mode does what
    const subtitle = isInOrgView
      ? `Inviting into ${activeOrg?.name || 'this organization'} — the user will join the linked customer tenant.`
      : isCustomerMode
        ? 'Choose which customer Org this user belongs to. They will join the customer\'s tenant, NOT your Provider tenant.'
        : isProvider
          ? 'Invite a teammate into your Provider tenant. They\'ll see Orgs/Companies based on the role and access you grant.'
          : null;
    return (
        <Card data-testid="invite-form-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-teal-500" />
              {isCustomerMode ? 'Invite a customer user' : 'Invite a teammate'}
              {isDemo && (
                <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700" data-testid="invite-demo-badge">
                  <Lock className="w-3 h-3" />
                  Demo Restricted
                </span>
              )}
            </CardTitle>
            {subtitle && (
              <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Mode tabs — only shown to Provider Admins outside an Org switcher
                view who have at least one linked-customer org */}
            {showCustomerInviteMode && (
              <div className="flex border-b border-gray-200" data-testid="invite-mode-tabs">
                {[
                  { id: 'teammate', label: 'Invite a teammate', help: 'Join your Provider tenant' },
                  { id: 'customer', label: 'Invite a customer user', help: `Join a linked customer's tenant` },
                ].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { setInviteMode(m.id); setInviteOrgId(''); setInviteRole('member'); }}
                    className={`px-3 py-2 text-xs font-medium transition-colors -mb-px ${
                      inviteMode === m.id
                        ? 'text-teal-600 border-b-2 border-teal-500'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title={m.help}
                    data-testid={`invite-mode-${m.id}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={handleInvite} className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[180px] space-y-1">
                <Label htmlFor="inv-email" className="text-xs">Email</Label>
                <Input id="inv-email" type="email" placeholder="user@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required data-testid="invite-email-input" />
              </div>
              <div className="w-40 space-y-1">
                <Label htmlFor="inv-name" className="text-xs">Name (optional)</Label>
                <Input id="inv-name" placeholder="Name" value={inviteName} onChange={(e) => setInviteName(e.target.value)} data-testid="invite-name-input" />
              </div>
              <div className="w-36 space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  Role
                  <RolePermissionsTooltip />
                </Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Customer-mode picker: REQUIRED, listing only linked-customer orgs */}
              {isCustomerMode && (
                <div className="w-52 space-y-1">
                  <Label className="text-xs">Customer Organization <span className="text-red-500">*</span></Label>
                  <Select value={inviteOrgId} onValueChange={setInviteOrgId}>
                    <SelectTrigger data-testid="invite-customer-org-select">
                      <SelectValue placeholder="Choose customer org…" />
                    </SelectTrigger>
                    <SelectContent>
                      {linkedOrgs.map(o => (
                        <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button
                type="submit"
                className="bg-teal-500 hover:bg-teal-600"
                disabled={inviting || (isCustomerMode && !inviteOrgId)}
                data-testid="invite-submit-btn"
              >
                {inviting ? 'Inviting...' : (isCustomerMode ? 'Send customer invite' : 'Send invite')}
              </Button>
            </form>
          </CardContent>
        </Card>
    );
  }

  function renderTeamMembers() {
    return (
        <Card data-testid="team-members-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Team Members ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {users.map((u) => (
                <div key={u.user_id} className={`flex flex-col md:flex-row md:items-center md:justify-between py-3 gap-2 ${u.is_active === false ? 'opacity-50' : ''}`} data-testid={`user-row-${u.user_id}`}>
                  <div className="flex items-center gap-3">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600 shrink-0">
                        {u.name?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {u.name}
                        {u.is_active === false && <span className="ml-2 text-[10px] font-semibold text-red-500 bg-red-50 px-1.5 py-0.5 rounded">Deactivated</span>}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pl-11 md:pl-0">
                    {u.user_id === user?.user_id ? (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[u.role]}`}>
                        {ROLE_LABELS[u.role]} (You)
                      </span>
                    ) : (
                      <>
                        <div className="flex items-center gap-1">
                          <Select value={u.role} onValueChange={(val) => handleRoleChange(u.user_id, val)}>
                            <SelectTrigger className="w-32 h-8 text-xs" data-testid={`role-select-${u.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {roleOptions.map((r) => (
                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <RolePermissionsTooltip />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-8 w-8 p-0 ${u.is_active === false ? 'text-green-500 hover:text-green-700 hover:bg-green-50' : 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'}`}
                          onClick={() => handleToggleStatus(u.user_id, u.name, u.is_active !== false)}
                          title={u.is_active === false ? 'Activate' : 'Deactivate'}
                          data-testid={`toggle-status-${u.user_id}`}
                        >
                          {u.is_active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0" onClick={() => handleRemoveUser(u.user_id, u.name, u.role)} data-testid={`remove-user-${u.user_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {users.length === 0 && (
                <p className="text-sm text-gray-500 py-4 text-center">No team members yet</p>
              )}
            </div>
          </CardContent>
        </Card>
    );
  }

  function renderPendingInvites() {
    if (isOperator && !inContext) return null;
    const pending = invites.filter((i) => !i.used);
    if (pending.length === 0) return null;

    // Group: "teammate" bucket + one bucket per customer Org (only meaningful
    // for Provider Admins; Solo/operator views fall into the single teammate
    // bucket which we hide-label when it's the only group).
    const teammate = pending.filter((i) => !i.is_customer_invite);
    const customerByOrg = pending
      .filter((i) => i.is_customer_invite)
      .reduce((acc, inv) => {
        const label = inv.org_name || inv.target_tenant_name || 'Linked customer';
        (acc[label] = acc[label] || []).push(inv);
        return acc;
      }, {});
    const customerGroups = Object.entries(customerByOrg).sort((a, b) => a[0].localeCompare(b[0]));
    const hasCustomerInvites = customerGroups.length > 0;

    const renderRow = (inv) => (
      <div key={inv.invite_id} className="flex items-center justify-between py-3" data-testid={`invite-row-${inv.invite_id}`}>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{inv.email}</p>
          <p className="text-xs text-gray-500">
            Role: {ROLE_LABELS[inv.role]} &middot; Invited {new Date(inv.created_at).toLocaleDateString()}
          </p>
        </div>
        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 shrink-0" onClick={() => handleDeleteInvite(inv.invite_id)} data-testid={`delete-invite-${inv.invite_id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    );

    return (
      <Card data-testid="pending-invites-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-500" />
            Pending Invites ({pending.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Teammate group — only label when there are also customer groups */}
          {teammate.length > 0 && (
            <div data-testid="invite-group-teammate">
              {hasCustomerInvites && (
                <div className="flex items-center gap-2 mb-1.5">
                  <UserPlus className="w-3.5 h-3.5 text-gray-400" />
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Your team
                  </h4>
                  <span className="text-[10px] text-gray-400">({teammate.length})</span>
                </div>
              )}
              <div className="divide-y">{teammate.map(renderRow)}</div>
            </div>
          )}
          {/* One section per linked customer Org */}
          {customerGroups.map(([label, list]) => (
            <div key={label} data-testid={`invite-group-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <Building2 className="w-3.5 h-3.5 text-teal-500" />
                <h4 className="text-xs font-semibold uppercase tracking-wider text-teal-700">
                  {label}
                </h4>
                <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">
                  {list.length} pending
                </span>
              </div>
              <div className="divide-y border border-teal-100 bg-teal-50/30 rounded-md px-3">
                {list.map(renderRow)}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }
}
